import { useRef, useState } from 'react';
import { useSelector } from 'react-redux'
import { useParams, Link } from 'react-router-dom'

import StatusOptionsMulti from '../components/psc/lib/StatusOptionsMulti'
import SearchBox from '../components/psc/lib/SearchBox'
import GncDataTable from '../components/psc/lib/GncDataTable'
import Loading from '../components/psc/lib/Loading'

import useAuthentication from '../hooks/useAuthentication';
import useUserManagement from '../hooks/useUserManagement';
import useTableSlots from '../hooks/useTableSlots';
import useProjectContents from '../hooks/useProjectContents';
import useDocumentStatuses from '../hooks/useDocumentStatuses';
import useBrowserTabTitle from '../hooks/useBrowserTabTitle'

const Documents = () => {
    const { projectID } = useParams()
    
    const pscState = useSelector(state => state.pscState.value)
    const project = pscState.project
    const isLoading = pscState.isLoading

    const table = useRef(null)
    const localStorageOrderKey = 'tblDocumentsOrder'
    const defaultOrder = [[ 2, 'asc' ]]
    
    const [isReloading, setIsReloading] = useState(false)

    const { currentUser } = useAuthentication();
    useBrowserTabTitle(`${project.project_code} Documents`)

    const {
        userHasProjectAccessLevel,
        isUserManagementInitialized
    } = useUserManagement()

    const {
        isDocumentsLoaded,
        documents,
        forceReloadDocuments,
    } = useProjectContents()

    const {
        documentStatuses
    } = useDocumentStatuses()

    const {
        dateSlot,
        filePathSlot,
        linkSlot,
        ownerSlot
    } = useTableSlots()

    const columns = [
        { data: 'final_version_file_path' },
        { data: 'created_at' },
        { data: 'reference' },
        { data: 'title' },
        { data: 'std_document.name' },
        { data: 'owner_user_id' },
        { data: 'document_status.name' }
    ]

    const tableHead = (
        <tr>
            <th style={{width: '25px'}}>&nbsp;</th>
            <th>Added</th>
            <th>Reference</th>
            <th>Title</th>
            <th>Type</th>
            <th>Owner</th>
            <th>Status</th>
        </tr>
    )
    
    const tableSlots = {
        0: (data, type, row) => filePathSlot(data || row.file_paths?.[0] || "", type, row),
        1: dateSlot,
        2: (data, type, row) => linkSlot(data, type, row, `/portfolio/${row.project_id}/documents/${row.id}`),
        3: (data, type, row) => linkSlot(data, type, row, `/portfolio/${row.project_id}/documents/${row.id}`),
        4: (data, type, row) => {
            if( type !== 'display') {
                return data
            }
            let value = data
            if (row.incorporated_in_projects?.length > 0) {
                value = (
                    <span>{data}
                        <i className="fa fa-share-alt" data-tooltip-id="gnctooltip" data-tooltip-content="Shared Document"></i>
                    </span>
                )
            }
            return value
        },
        5: ownerSlot
    }
    
    const handleReload = async () => {
        setIsReloading(true)
        try {
            await forceReloadDocuments()
        } finally {
            setIsReloading(false)
        }
    }

    const project_type_name = project?.project_type?.name || 'Project'
    const showLoading = isLoading || !isUserManagementInitialized || !isDocumentsLoaded
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">

                        <div className="ibox-title">
                            <h2>Documents</h2>

                            { project && userHasProjectAccessLevel(currentUser, project.id, 'MEMBER') &&
                              <Link to={`/portfolio/${projectID}/documents/new`} type="button" className="btn btn-mr btn-primary">New Document</Link>
                            }
                            
                            <button 
                                type="button" 
                                className="btn btn-link pull-right" 
                                onClick={handleReload}
                                disabled={isReloading}
                                style={{ padding: '0 10px', marginTop: '-5px' }}
                                data-tooltip-id="gnctooltip" 
                                data-tooltip-content="Reload Documents"
                            >
                                <i className={`fa fa-refresh ${isReloading ? 'fa-spin' : ''}`} style={{ fontSize: '14px', color: 'rgba(103, 106, 108, 0.65)' }}></i>
                            </button>

                                {<div key="newText1" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
                                  Documents are <b>version-controlled</b>. References are <b>automatically created</b>, based on this {project_type_name}'s configuration scheme.
                                 </div>
                                }
                        </div>

                        <div className="ibox-content">
                            <div className="row">
                                <SearchBox tableRef={table} />
                                <StatusOptionsMulti
                                    tableRef={table}
                                    statusColumnIndex="6"
                                    data={documentStatuses}
                                    localStorageKey="document-list-statuses"
                                />
                            </div>

                            { showLoading ? <Loading /> : (
                                <GncDataTable
                                    tableRef={table}
                                    data={documents}
                                    defaultOrder={defaultOrder}
                                    localStorageOrderKey={localStorageOrderKey}
                                    columns={columns}
                                    tableSlots={tableSlots}
                                    tableHead={tableHead}
                                    tableFoot={tableHead}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Documents
